"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { memberCountLabel } from "@/lib/counts";
import { groupDocumentKey, useV1Group } from "@/lib/hooks/useGroupPage";
import { useMyGroups } from "@/lib/hooks/useMyGroups";
import { useSetFavourite } from "@/lib/hooks/useMembers";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { useGroupBot } from "@/lib/hooks/useGroupBot";
import { useQueryClient } from "@tanstack/react-query";
import type { Group } from "@theta/api-client";

const MAX_GROUPS = 10;

/**
 * EVERY ADMIN LINK CARRIES THE CAPABILITY ITS PAGE DEMANDS
 *
 * Both menus were gated once, on `can.moderate`, and then listed pages this
 * batch moved to `can.administer` — Statistik, Invitationer, Massepost,
 * Gruppeindstillinger, Undergrupper and Faner. A group MODERATOR was shown all
 * six; clicking one ran the page's own `router.replace(`/grupper/${slug}`)`
 * and threw them out of the admin area with no message, and the sidebar then
 * collapsed out of admin mode behind them. Four of those pages accepted
 * moderators BEFORE this batch, so it is a regression rather than a
 * tightening — and the affordance was simply not moved with the gate.
 *
 * `requires` is that gate, spelled once per item, read from `group.can` — the
 * same predicate the page enforces and the route behind it enforces (D11).
 * Inherited parent-group admins are unaffected in either direction:
 * `canModerate`/`canAdminister` already answer for them. Platform staff hold
 * no group role by being staff (OSO-517), so a site admin sees this panel
 * only in groups they actually administer or moderate.
 */
type Capability = "moderate" | "administer";

type NavItem = {
  label: string;
  path: string;
  /** The `group.can` key the page behind this link demands. */
  requires: Capability;
  icon: React.ReactNode;
  count?: number;
};

const GRUPPE_ITEMS: NavItem[] = [
  {
    label: "Statistik",
    path: "statistik",
    requires: "administer",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
  },
  {
    label: "Ventende anmodninger",
    path: "anmodninger",
    requires: "moderate",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
  },
  {
    label: "Indhold & moderering",
    path: "moderering",
    requires: "moderate",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    label: "Spam-anmeldelser",
    path: "spam",
    requires: "moderate",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <line x1="4" y1="22" x2="4" y2="15" />
      </svg>
    ),
  },
  {
    label: "Invitationer",
    path: "invitationer",
    requires: "administer",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
  },
];

const INDSTILLINGER_ITEMS: NavItem[] = [
  {
    label: "Medlemmer & roller",
    path: "medlemmer",
    requires: "moderate",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    label: "Gruppeindstillinger",
    path: "indstillinger",
    requires: "administer",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
  },
  {
    label: "Bot",
    path: "bot",
    requires: "administer",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="10" rx="2" />
        <circle cx="12" cy="5" r="2" />
        <path d="M12 7v4" />
        <line x1="8" y1="16" x2="8" y2="16" />
        <line x1="16" y1="16" x2="16" y2="16" />
      </svg>
    ),
  },
  {
    label: "Undergrupper",
    path: "undergrupper",
    requires: "administer",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="6" height="4" rx="1" />
        <rect x="9" y="11" width="6" height="4" rx="1" />
        <rect x="16" y="17" width="6" height="4" rx="1" />
        <line x1="5" y1="7" x2="5" y2="13" />
        <line x1="5" y1="13" x2="12" y2="13" />
        <line x1="12" y1="15" x2="12" y2="19" />
        <line x1="12" y1="19" x2="16" y2="19" />
      </svg>
    ),
  },
  {
    label: "Faner",
    path: "faner",
    requires: "administer",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
  },
];

// Only relevant to a parent group (one with sub-groups): broadcast a post into
// all of them at once.
const BROADCAST_ITEM: NavItem = {
  label: "Massepost",
  path: "massepost",
  requires: "administer",
  icon: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  ),
};

/**
 * Pending-item badges, keyed by the nav path they belong on.
 *
 * `pendingCounts` is ONE nullable stanza on the group document instead of
 * three optional top-level numbers, and it is `null` for a viewer who may not
 * work those queues — so a moderator's badges are the server's answer to "what
 * is waiting for YOU", not three fields that happened to be serialized.
 */
type PendingCounts = NonNullable<Group["pendingCounts"]>;
const PENDING_COUNT_KEYS: Record<string, keyof PendingCounts> = {
  anmodninger: "requests",
  moderering: "flags",
  spam: "spamReports",
};

/** The items this viewer may actually open, badged with what is waiting. */
function permitted(items: NavItem[], group?: Group | null): NavItem[] {
  if (!group) return [];
  return items
    .filter((item) => group.can[item.requires])
    .map((item) => {
      const countKey = PENDING_COUNT_KEYS[item.path];
      const count = countKey ? group.pendingCounts?.[countKey] : undefined;
      return count ? { ...item, count } : item;
    });
}

function gruppeItemsFor(group?: Group | null): NavItem[] {
  const items = group?.subGroupCount ? [...GRUPPE_ITEMS, BROADCAST_ITEM] : GRUPPE_ITEMS;
  return permitted(items, group);
}

/**
 * ONE ITEM IS NOT DECIDED BY A CAPABILITY AT ALL
 *
 * Group bots are an EXPERIMENT, off for every group until an operator turns
 * them on, and `group.can` says nothing about it — so "Bot" was drawn for
 * every group administrator on the platform. Following it opened a page that
 * offered to create a bot and then failed the save with a bare "Kunne ikke
 * gemme", because all six bot routes answer 404 for a group outside the
 * experiment. That is precisely the discovery the 404-not-403 gate was chosen
 * to prevent, undone by the affordance in front of it.
 *
 * `GET /groups/{ref}/bot` IS the test, and there is no cheaper one: the flag
 * is not on the group document, and putting it there would publish the list of
 * who is in the experiment to every reader of every group. So the item is
 * drawn only once that read has SUCCEEDED — asked at all only for a viewer who
 * administers, since nobody else can see the item anyway.
 */
function useBotNavAvailable(slug: string | null, group?: Group | null): boolean {
  const canAdminister = group?.can.administer ?? false;
  const { isSuccess } = useGroupBot(slug ?? "", canAdminister);
  return canAdminister && isSuccess;
}

function indstillingerItemsFor(group: Group | null | undefined, botAvailable: boolean): NavItem[] {
  const items = botAvailable
    ? INDSTILLINGER_ITEMS
    : INDSTILLINGER_ITEMS.filter((item) => item.path !== "bot");
  return permitted(items, group);
}

function StarIcon({ filled }: { filled: boolean }) {
  if (filled) {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-subtle-foreground">
      {children}
    </p>
  );
}

function NavLink({ item, slug }: { item: NavItem; slug: string }) {
  const pathname = usePathname();
  const href = `/grupper/${slug}/admin/${item.path}`;
  const isActive = pathname === href || pathname.startsWith(`${href}/`);

  return (
    <Link
      href={href}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
        isActive
          ? "bg-indigo-50 dark:bg-indigo-900/30 text-primary-muted-foreground font-semibold"
          : "text-foreground hover:bg-surface-2"
      }`}
    >
      <span className={`shrink-0 ${isActive ? "text-primary" : "text-subtle-foreground"}`}>
        {item.icon}
      </span>
      <span className="flex-1 truncate">{item.label}</span>
      {!!item.count && (
        <span className="shrink-0 min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center rounded-full bg-indigo-600 text-white text-xs font-medium">
          {item.count}
        </span>
      )}
    </Link>
  );
}

export function UnifiedGroupSidebar({ slug }: { slug: string | null }) {
  const queryClient = useQueryClient();
  // Read cache synchronously so initial state is correct on remount — prevents the admin
  // menu from animating in again when the component remounts with already-cached data.
  // The SAME key `useV1Group` writes, so the synchronous cache read still
  // works after the by-slug hook was deleted (D8: the slug IS the address).
  const cachedGroup = queryClient.getQueryData<Group>(groupDocumentKey(slug ?? ""));
  const isAdminAtMount = !!slug && (cachedGroup?.can.moderate ?? false);

  const { data: group, isLoading } = useV1Group(slug ?? "");
  const botAvailable = useBotNavAvailable(slug, group);
  const { data: groups } = useMyGroups();
  const { data: currentUser } = useCurrentUser();
  const setFavourite = useSetFavourite();
  const pathname = usePathname();

  // `can.moderate`, from the predicate the routes enforce (D11) — a parent
  // group's admin moderates here without holding a row, which the old role
  // test missed.
  const isAdmin = !!slug && (group?.can.moderate ?? false);

  const [adminMode, setAdminMode] = useState(isAdminAtMount);
  const [listExpanded, setListExpanded] = useState(false);
  const [adminMenuVisible, setAdminMenuVisible] = useState(isAdminAtMount);
  const [ready, setReady] = useState(!!cachedGroup || !isLoading);

  useEffect(() => {
    if (isLoading) {
      // New slug being fetched — suppress transitions until data arrives
      // TODO: the ready/adminMenuVisible pair plus staggered timeouts is a small
      // state machine that would read better as a reducer. Left as-is deliberately:
      // it drives the sidebar enter/exit transitions and is easy to regress.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setReady(false);
      return;
    }

    // Data has arrived (or was already cached). Jump directly to final state without
    // staggered timeouts when we weren't ready yet (first visit to uncached group).
    if (!ready) {
      setReady(true);
      if (isAdmin) {
        setAdminMode(true);
        setListExpanded(false);
        setAdminMenuVisible(true);
      } else {
        setAdminMode(false);
        setListExpanded(false);
        setAdminMenuVisible(false);
      }
      return;
    }

    // Data was already cached — use the normal animated transitions for intentional
    // state changes (e.g. navigating between two already-visited groups).
    if (isAdmin) {
      setAdminMode(true);
      setListExpanded(true);
      const t1 = setTimeout(() => setListExpanded(false), 50);
      const t2 = setTimeout(() => setAdminMenuVisible(true), 300);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    } else {
      setAdminMenuVisible(false);
      const t = setTimeout(() => {
        setAdminMode(false);
        setListExpanded(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isAdmin, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const gruppeItems = gruppeItemsFor(group);
  const indstillingerItems = indstillingerItemsFor(group, botAvailable);

  // Server-ordered (§1.4) — `?sort=favourite`. See the note in
  // `GroupSidebar`: re-sorting the array here was only sound while the list
  // was guaranteed complete, which cursor paging ends.
  const myGroups = groups ?? [];
  const visibleGroups = myGroups.slice(0, MAX_GROUPS);

  /**
   * THE HEADER SLOT IS NEVER EMPTY WHILE THE NEXT GROUP LOADS (OSO-553).
   *
   * `group` is the group DOCUMENT, refetched on every slug change, and this
   * collapsed header is the only thing in that slot. Rendering nothing until
   * the document arrived collapsed the slot to zero height, and the list —
   * still open, because opening it is how you picked a group — slid up into
   * it: for the length of the request the TOP row of the list sat where the
   * switcher's title belongs, so it read as the group you had just chosen.
   *
   * The list already carries a full document for every group the viewer is an
   * ACTIVE MEMBER of, so for the ordinary switch — a group you are in, picked
   * from the list you picked it in — the name and the cover are in memory
   * before the request is sent.
   *
   * It is NOT every group that can be in this slot. `can.moderate` is what
   * puts the sidebar in admin mode, and an inherited parent-group admin holds
   * that over a sub-group WITHOUT a membership row (`groups/access.ts`), so
   * they are missing from `?member=me` and land here with nothing to render —
   * which is why the placeholder below is a floor under the slot rather than
   * a nicety: it holds the height for any case where neither source has the
   * group yet, including ones nobody has thought of.
   */
  const headerGroup = group ?? myGroups.find((g) => g.slug === slug);

  /** A VALUE, not a toggle — see `useSetFavourite`. */
  function handleToggleFavourite(e: React.MouseEvent, ref: string, next: boolean) {
    e.preventDefault();
    e.stopPropagation();
    setFavourite.mutate({ ref, isFavourite: next });
  }

  return (
    <div className="w-72 flex flex-col shrink-0">
      <div className="bg-surface rounded-xl border border-border overflow-hidden">

        {/* Normal header — "Grupper" — animates away in admin mode */}
        <div
          style={{
            maxHeight: adminMode ? "0px" : "80px",
            opacity: adminMode ? 0 : 1,
            overflow: "hidden",
            transition: ready ? "max-height 0.4s linear, opacity 0.3s ease" : "none",
          }}
        >
          <div className="p-4 border-b border-border">
            <Link href="/grupper">
              <h1 className="text-xl font-bold text-foreground">Grupper</h1>
              <p className="text-xs text-subtle-foreground mt-0.5">Dine fællesskaber</p>
            </Link>
          </div>
        </div>

        {/* Admin collapsed header — animates in when adminMode */}
        <div
          style={{
            maxHeight: adminMode ? "80px" : "0px",
            opacity: adminMode ? 1 : 0,
            overflow: "hidden",
            transition: ready ? "max-height 0.4s linear, opacity 0.3s ease" : "none",
          }}
        >
          {headerGroup ? (
            <button
              onClick={() => setListExpanded((v) => !v)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-2 cursor-pointer transition-colors"
            >
              {headerGroup.coverImageUrl ? (
                <img
                  src={headerGroup.coverImageUrl}
                  alt=""
                  className="w-10 h-10 rounded-xl object-cover shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-xl bg-primary-muted/50 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                  {headerGroup.name[0]}
                </div>
              )}
              <p className="flex-1 min-w-0 text-sm font-medium text-foreground truncate text-left">
                {headerGroup.name}
              </p>
              <span
                className="shrink-0 text-subtle-foreground"
                style={{
                  transform: listExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.4s linear",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
          ) : (
            <div className="flex items-center gap-3 px-4 py-3" aria-hidden="true" data-testid="switcher-placeholder">
              <div className="w-10 h-10 rounded-xl bg-muted animate-pulse shrink-0" />
              <div className="flex-1 h-3 rounded bg-muted animate-pulse" />
            </div>
          )}
        </div>

        {/* Group list — collapses when in admin mode (unless listExpanded) */}
        <div
          style={{
            maxHeight: !adminMode || listExpanded ? "900px" : "0px",
            overflow: "hidden",
            transition: ready ? "max-height 0.4s linear" : "none",
          }}
        >
          <div className={adminMode ? "border-t border-border" : ""}>
            {visibleGroups.map((g) => {
              const href = `/grupper/${g.slug}`;
              const isActive = g.slug === slug;
              const isFavourite = g.isFavourite;
              return (
                <Link
                  key={g.id}
                  href={href}
                  /**
                   * `page` only when this link IS the page. Deeper in the
                   * group — `/grupper/<slug>/opslag/7` — the row points at an
                   * ancestor section, and announcing "current page" for a link
                   * that navigates away is the wrong claim; `true` is the value
                   * for that.
                   */
                  aria-current={isActive ? (pathname === href ? "page" : true) : undefined}
                  className={`group w-full text-left px-4 py-3 flex items-center gap-3 transition-colors block ${
                    isActive
                      ? "bg-primary-muted"
                      : "hover:bg-surface-2"
                  }`}
                >
                  {g.coverImageUrl ? (
                    <img
                      src={g.coverImageUrl}
                      alt=""
                      className="w-10 h-10 rounded-xl object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-xl bg-primary-muted/50 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                      {g.name[0]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-medium truncate ${
                        isActive ? "text-indigo-900 dark:text-indigo-100" : "text-foreground"
                      }`}
                    >
                      {g.name}
                    </p>
                    <p className="text-xs text-subtle-foreground">
                      {memberCountLabel(g.memberCount)}
                    </p>
                  </div>
                  <button
                    onClick={(e) => handleToggleFavourite(e, g.slug, !isFavourite)}
                    className={`flex-shrink-0 p-1 rounded transition-colors ${
                      isFavourite
                        ? "text-amber-400 hover:text-amber-500"
                        : "pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 pointer-fine:focus-visible:opacity-100 text-gray-300 hover:text-amber-400"
                    }`}
                    title={isFavourite ? "Fjern fra favoritter" : "Tilføj til favoritter"}
                  >
                    <StarIcon filled={isFavourite} />
                  </button>
                  {g.unreadCount > 0 && (
                    <span className="ml-2 shrink-0 min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center rounded-full bg-indigo-600 text-white text-xs font-medium">
                      {g.unreadCount}
                    </span>
                  )}
                </Link>
              );
            })}

            <Link
              href="/begivenheder"
              className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors block border-t border-border ${
                pathname.startsWith("/begivenheder")
                  ? "bg-primary-muted"
                  : "hover:bg-surface-2"
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                pathname.startsWith("/begivenheder")
                  ? "bg-primary-muted/50 text-primary"
                  : "bg-muted text-muted-foreground"
              }`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${
                  pathname.startsWith("/begivenheder") ? "text-indigo-900 dark:text-indigo-100" : "text-foreground"
                }`}>
                  Begivenheder
                </p>
                <p className="text-xs text-subtle-foreground">Kommende events</p>
              </div>
            </Link>

            <div className="p-3 border-t border-border space-y-2">
              {currentUser && (
                <Link
                  href="/grupper/opret"
                  className="flex items-center justify-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Opret gruppe
                </Link>
              )}
              <Link
                href="/grupper"
                className="flex items-center justify-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-primary bg-primary-muted hover:bg-indigo-100 dark:hover:bg-indigo-900 rounded-lg transition-colors"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                Opdag nye grupper
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          maxHeight: adminMenuVisible ? "800px" : "0px",
          opacity: adminMenuVisible ? 1 : 0,
          marginTop: adminMenuVisible ? "12px" : "0px",
          overflow: "hidden",
          transition: ready ? "max-height 0.4s linear, opacity 0.3s ease, margin-top 0.4s linear" : "none",
        }}
      >
        <div className="bg-surface rounded-xl border border-border">
          <div className="p-4 border-b border-border">
            <h2 className="text-base font-bold text-foreground">Administration</h2>
          </div>

          <div className="pb-4">
            {slug && gruppeItems.length > 0 && (
              <>
                <SectionLabel>Gruppe</SectionLabel>
                {gruppeItems.map((item) => (
                  <NavLink key={item.path} item={item} slug={slug} />
                ))}
              </>
            )}

            {slug && indstillingerItems.length > 0 && (
              <>
                <SectionLabel>Indstillinger</SectionLabel>
                {indstillingerItems.map((item) => (
                  <NavLink key={item.path} item={item} slug={slug} />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function GroupAdminMobileNav({ slug }: { slug: string | null }) {
  const pathname = usePathname();
  const { data: group } = useV1Group(slug ?? "");
  // Before the early return, like every other hook — the sidebar above shares
  // this query, so on a group page the two components pay for it once.
  const botAvailable = useBotNavAvailable(slug, group);

  const isAdmin = !!slug && (group?.can.moderate ?? false);

  const isOnAdminPage = !!slug && pathname.startsWith(`/grupper/${slug}/admin/`);

  if (!isAdmin || !isOnAdminPage) return null;

  const allItems = [...gruppeItemsFor(group), ...indstillingerItemsFor(group, botAvailable)];

  return (
    <div className="lg:hidden bg-surface rounded-xl border border-border overflow-hidden">
      <div className="flex overflow-x-auto no-scrollbar">
        {allItems.map((item) => {
          const isActive = pathname.startsWith(`/grupper/${slug}/admin/${item.path}`);
          return (
            <Link
              key={item.path}
              href={`/grupper/${slug}/admin/${item.path}`}
              className={`flex-none flex flex-col items-center gap-1 px-4 py-3 text-xs transition-colors border-b-2 ${
                isActive
                  ? "border-indigo-600 dark:border-indigo-400 text-primary-muted-foreground font-semibold"
                  : "border-transparent text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <span className="relative">
                <span className={isActive ? "text-primary" : "text-subtle-foreground"}>
                  {item.icon}
                </span>
                {!!item.count && (
                  <span className="absolute -top-1.5 -right-2 min-w-[1.1rem] h-[1.1rem] px-1 flex items-center justify-center rounded-full bg-indigo-600 text-white text-[10px] font-medium leading-none">
                    {item.count}
                  </span>
                )}
              </span>
              {item.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
